<template>
  <div class="client-dashboard" :class="{ 'client-dashboard--dark': isDarkSkin }">

    <div class="client-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" default-option="thisMonth" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- ===== KPI summary ===== -->
      <b-row class="kpi-row">
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--primary">
            <div class="kpi-card__icon"><feather-icon icon="UsersIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('clientDashboard.totalClients') }}</div>
              <div class="kpi-card__value">{{ grandTotal.total | number }}</div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon"><feather-icon icon="LogInIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('clientDashboard.ftd') }}</div>
              <div class="kpi-card__value">{{ grandTotal.ftd | number }}</div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--warning">
            <div class="kpi-card__icon"><feather-icon icon="RepeatIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('clientDashboard.std') }}</div>
              <div class="kpi-card__value">{{ grandTotal.std | number }}</div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
        <b-col xl="3" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--info">
            <div class="kpi-card__icon"><feather-icon icon="PercentIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('clientDashboard.stdPct') }}</div>
              <div class="kpi-card__value">{{ pct(grandTotal.stdPct) }}</div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <!-- ===== Table 1 — Client Aging ===== -->
      <div class="panel-card mt-1">
        <div class="panel-card__header">
          <div class="panel-card__title-wrap">
            <div class="panel-card__icon-chip"><feather-icon icon="ClockIcon" size="18"/></div>
            <h5 class="panel-card__title mb-0">{{ $t('clientDashboard.agingTitle') }}</h5>
          </div>
          <small class="text-muted">{{ $t('clientDashboard.byLastNote') }}</small>
        </div>
        <div class="panel-card__body p-0">
          <div class="cd-table-wrap">
            <table class="cd-table">
              <thead>
                <tr>
                  <th class="cd-th--brand">{{ $t('clientDashboard.brand') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.age0to15') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.age15to30') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.age30plus') }}</th>
                  <th class="text-right cd-th--total">{{ $t('clientDashboard.total') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="g in groups">
                  <tr :key="`a-h-${g.desk}`" class="cd-group">
                    <td colspan="5">
                      <div class="cd-group__row">
                        <span class="cd-group__chip"><i class="fa-solid fa-layer-group mr-50"></i>{{ g.desk }}</span>
                        <span class="cd-group__count">{{ g.subtotal.total | number }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="b in g.brands" :key="`a-${g.desk}-${b.orgId}`" class="cd-brand">
                    <td class="cd-td--brand">
                      <span class="cd-brand-name">{{ b.name }}</span>
                      <button v-if="canCreateTask" type="button" class="cd-send-btn"
                              v-b-tooltip.hover :title="$t('tasks.sendReport')"
                              @click="sendReport(b)">
                        <i class="fa-solid fa-paper-plane"></i>
                      </button>
                    </td>
                    <td class="text-right"><drill-cell chip variant="good" :count="b.age0to15" @drill="drill([b.orgId], 'age0to15', b.name, b.age0to15)" @external="openExternal([b.orgId], 'age0to15', b.name, b.age0to15)"/></td>
                    <td class="text-right"><drill-cell chip variant="warn" :count="b.age15to30" @drill="drill([b.orgId], 'age15to30', b.name, b.age15to30)" @external="openExternal([b.orgId], 'age15to30', b.name, b.age15to30)"/></td>
                    <td class="text-right"><drill-cell chip variant="bad" :count="b.age30plus" @drill="drill([b.orgId], 'age30plus', b.name, b.age30plus)" @external="openExternal([b.orgId], 'age30plus', b.name, b.age30plus)"/></td>
                    <td class="text-right cd-td--total"><drill-cell :count="b.total" @drill="drill([b.orgId], 'total', b.name, b.total)" @external="openExternal([b.orgId], 'total', b.name, b.total)"/></td>
                  </tr>
                  <tr :key="`a-s-${g.desk}`" class="cd-subtotal">
                    <td>{{ $t('clientDashboard.subtotal') }}</td>
                    <td class="text-right"><drill-cell :count="g.subtotal.age0to15" @drill="drill(deskOrgIds(g), 'age0to15', g.desk, g.subtotal.age0to15)" @external="openExternal(deskOrgIds(g), 'age0to15', g.desk, g.subtotal.age0to15)"/></td>
                    <td class="text-right"><drill-cell :count="g.subtotal.age15to30" @drill="drill(deskOrgIds(g), 'age15to30', g.desk, g.subtotal.age15to30)" @external="openExternal(deskOrgIds(g), 'age15to30', g.desk, g.subtotal.age15to30)"/></td>
                    <td class="text-right"><drill-cell :count="g.subtotal.age30plus" @drill="drill(deskOrgIds(g), 'age30plus', g.desk, g.subtotal.age30plus)" @external="openExternal(deskOrgIds(g), 'age30plus', g.desk, g.subtotal.age30plus)"/></td>
                    <td class="text-right"><drill-cell :count="g.subtotal.total" @drill="drill(deskOrgIds(g), 'total', g.desk, g.subtotal.total)" @external="openExternal(deskOrgIds(g), 'total', g.desk, g.subtotal.total)"/></td>
                  </tr>
                </template>
                <tr v-if="!groups.length"><td colspan="5" class="text-center text-muted py-2">{{ $t('clientDashboard.noData') }}</td></tr>
              </tbody>
              <tfoot v-if="groups.length">
                <tr class="cd-grand">
                  <td>{{ $t('clientDashboard.grandTotal') }}</td>
                  <td class="text-right"><drill-cell :count="grandTotal.age0to15" @drill="drill(allOrgIds, 'age0to15', $t('clientDashboard.grandTotal'), grandTotal.age0to15)" @external="openExternal(allOrgIds, 'age0to15', $t('clientDashboard.grandTotal'), grandTotal.age0to15)"/></td>
                  <td class="text-right"><drill-cell :count="grandTotal.age15to30" @drill="drill(allOrgIds, 'age15to30', $t('clientDashboard.grandTotal'), grandTotal.age15to30)" @external="openExternal(allOrgIds, 'age15to30', $t('clientDashboard.grandTotal'), grandTotal.age15to30)"/></td>
                  <td class="text-right"><drill-cell :count="grandTotal.age30plus" @drill="drill(allOrgIds, 'age30plus', $t('clientDashboard.grandTotal'), grandTotal.age30plus)" @external="openExternal(allOrgIds, 'age30plus', $t('clientDashboard.grandTotal'), grandTotal.age30plus)"/></td>
                  <td class="text-right"><drill-cell :count="grandTotal.total" @drill="drill(allOrgIds, 'total', $t('clientDashboard.grandTotal'), grandTotal.total)" @external="openExternal(allOrgIds, 'total', $t('clientDashboard.grandTotal'), grandTotal.total)"/></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== Table 2 — FTD / STD conversion ===== -->
      <div class="panel-card mt-2">
        <div class="panel-card__header">
          <div class="panel-card__title-wrap">
            <div class="panel-card__icon-chip panel-card__icon-chip--alt"><feather-icon icon="TrendingUpIcon" size="18"/></div>
            <h5 class="panel-card__title mb-0">{{ $t('clientDashboard.conversionTitle') }}</h5>
          </div>
          <small class="text-muted">{{ rangeLabel }}</small>
        </div>
        <div class="panel-card__body p-0">
          <div class="cd-table-wrap">
            <table class="cd-table">
              <thead>
                <tr>
                  <th class="cd-th--brand">{{ $t('clientDashboard.brand') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.ftd') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.std') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.stdPct') }}</th>
                  <th class="text-right">{{ $t('clientDashboard.neverStd') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="g in groups">
                  <tr :key="`c-h-${g.desk}`" class="cd-group">
                    <td colspan="5">
                      <div class="cd-group__row">
                        <span class="cd-group__chip"><i class="fa-solid fa-layer-group mr-50"></i>{{ g.desk }}</span>
                        <span class="cd-group__count">{{ g.subtotal.ftd | number }} {{ $t('clientDashboard.ftd') }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="b in g.brands" :key="`c-${g.desk}-${b.orgId}`" class="cd-brand">
                    <td class="cd-td--brand">{{ b.name }}</td>
                    <td class="text-right cd-strong"><drill-cell :count="b.ftd" @drill="drill([b.orgId], 'ftd', b.name, b.ftd)" @external="openExternal([b.orgId], 'ftd', b.name, b.ftd)"/></td>
                    <td class="text-right"><drill-cell :count="b.std" @drill="drill([b.orgId], 'std', b.name, b.std)" @external="openExternal([b.orgId], 'std', b.name, b.std)"/></td>
                    <td>
                      <div class="cd-bar">
                        <div class="cd-bar__track"><div class="cd-bar__fill" :class="pctClass(b.stdPct)" :style="{ width: barW(b.stdPct) }"></div></div>
                        <span class="cd-bar__val">{{ pct(b.stdPct) }}</span>
                      </div>
                    </td>
                    <td class="text-right"><drill-cell :count="b.neverStd" @drill="drill([b.orgId], 'neverStd', b.name, b.neverStd)" @external="openExternal([b.orgId], 'neverStd', b.name, b.neverStd)"/></td>
                  </tr>
                  <tr :key="`c-s-${g.desk}`" class="cd-subtotal">
                    <td>{{ $t('clientDashboard.subtotal') }}</td>
                    <td class="text-right"><drill-cell :count="g.subtotal.ftd" @drill="drill(deskOrgIds(g), 'ftd', g.desk, g.subtotal.ftd)" @external="openExternal(deskOrgIds(g), 'ftd', g.desk, g.subtotal.ftd)"/></td>
                    <td class="text-right"><drill-cell :count="g.subtotal.std" @drill="drill(deskOrgIds(g), 'std', g.desk, g.subtotal.std)" @external="openExternal(deskOrgIds(g), 'std', g.desk, g.subtotal.std)"/></td>
                    <td class="text-right">{{ pct(g.subtotal.stdPct) }}</td>
                    <td class="text-right"><drill-cell :count="g.subtotal.neverStd" @drill="drill(deskOrgIds(g), 'neverStd', g.desk, g.subtotal.neverStd)" @external="openExternal(deskOrgIds(g), 'neverStd', g.desk, g.subtotal.neverStd)"/></td>
                  </tr>
                </template>
                <tr v-if="!groups.length"><td colspan="5" class="text-center text-muted py-2">{{ $t('clientDashboard.noData') }}</td></tr>
              </tbody>
              <tfoot v-if="groups.length">
                <tr class="cd-grand">
                  <td>{{ $t('clientDashboard.grandTotal') }}</td>
                  <td class="text-right"><drill-cell :count="grandTotal.ftd" @drill="drill(allOrgIds, 'ftd', $t('clientDashboard.grandTotal'), grandTotal.ftd)" @external="openExternal(allOrgIds, 'ftd', $t('clientDashboard.grandTotal'), grandTotal.ftd)"/></td>
                  <td class="text-right"><drill-cell :count="grandTotal.std" @drill="drill(allOrgIds, 'std', $t('clientDashboard.grandTotal'), grandTotal.std)" @external="openExternal(allOrgIds, 'std', $t('clientDashboard.grandTotal'), grandTotal.std)"/></td>
                  <td class="text-right">{{ pct(grandTotal.stdPct) }}</td>
                  <td class="text-right"><drill-cell :count="grandTotal.neverStd" @drill="drill(allOrgIds, 'neverStd', $t('clientDashboard.grandTotal'), grandTotal.neverStd)" @external="openExternal(allOrgIds, 'neverStd', $t('clientDashboard.grandTotal'), grandTotal.neverStd)"/></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </b-overlay>

    <!-- Drill-down: clients behind a clicked cell -->
    <client-dashboard-list-modal ref="listModal"/>
    <send-report-task-modal ref="sendTaskModal"/>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import FilterCard from '@/components/widget/FilterCard';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import { mapActions, mapGetters } from 'vuex';
import ClientDashboardSearchRequest from '@/model/Dashboard/ClientDashboardSearchRequest';
import ClientDashboardFilterFields from '@/model/filter/ClientDashboardFilterFields';
import ClientDashboardListModal from '@/components/modal/ClientDashboardListModal';
import DrillCell from '@/components/dashboard/DrillCell';
import SendReportTaskModal from '@/components/modal/SendReportTaskModal';
import { compareDesks } from '@/model/Utils';

const REFRESH_DEBOUNCE_MS = 250;

const ZERO = () => ({ total: 0, age0to15: 0, age15to30: 0, age30plus: 0, ftd: 0, std: 0, neverStd: 0, stdPct: 0 });

export default {
  name: 'ClientDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, FilterCard, ClientDashboardListModal, SendReportTaskModal, DrillCell },
  filters: {
    number(v) {
      return new Intl.NumberFormat().format(Math.round(v || 0));
    },
  },
  data: () => ({
    filter: ClientDashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: '', until: '' },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    rows: [],
  }),
  watch: {
    filter: { handler: 'scheduleRefresh', deep: true },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['allOrganizations']),
    viewKey() {
      return this.$route.name || 'client-dashboard';
    },
    rangeLabel() {
      if (!this.dateRange.from && !this.dateRange.until) return this.$t('clientDashboard.allTime');
      const f = this.dateRange.from ? this.$moment(this.dateRange.from).format('DD/MM/YYYY') : '…';
      const u = this.dateRange.until ? this.$moment(this.dateRange.until).format('DD/MM/YYYY') : '…';
      return `${f} → ${u}`;
    },
    rowsByOrg() {
      const m = {};
      (this.rows || []).forEach(r => { m[r.orgId] = r; });
      return m;
    },
    targetOrgs() {
      const all = this.allOrganizations || [];
      const sel = this.filter.orgIds;
      if (Array.isArray(sel) && sel.length) return all.filter(o => sel.includes(o.id));
      return all;
    },
    groups() {
      const byOrg = this.rowsByOrg;
      const deskMap = new Map();
      this.targetOrgs.forEach(o => {
        const desk = (o.desk && String(o.desk).trim()) ? o.desk : this.$t('clientDashboard.noDesk');
        const r = byOrg[o.id] || {};
        const ftd = r.ftd || 0;
        const std = r.std || 0;
        const brand = {
          orgId: o.id,
          name: o.name || o.id,
          total: r.total || 0,
          age0to15: r.age0to15 || 0,
          age15to30: r.age15to30 || 0,
          age30plus: r.age30plus || 0,
          ftd,
          std,
          neverStd: Math.max(0, ftd - std),
          stdPct: ftd > 0 ? (std / ftd) * 100 : 0,
        };
        if (!deskMap.has(desk)) deskMap.set(desk, { desk, brands: [] });
        deskMap.get(desk).brands.push(brand);
      });
      const groups = [...deskMap.values()].sort((a, b) => compareDesks(a.desk, b.desk));
      groups.forEach(g => {
        g.brands.sort((a, b) => a.name.localeCompare(b.name));
        g.subtotal = this.sumRows(g.brands);
      });
      return groups;
    },
    grandTotal() {
      return this.sumRows(this.groups.flatMap(g => g.brands));
    },
    canCreateTask() {
      return this.hasPermission(this.permissions.TASK_CREATE);
    },
    allOrgIds() {
      return this.groups.flatMap(g => g.brands.map(b => b.orgId));
    },
  },
  created() {
    this.filterFields = ClientDashboardFilterFields();
  },
  mounted() {
    this.$root.$on('dashboard::refresh', this.scheduleRefresh);
    this.scheduleRefresh();
  },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  },
  methods: {
    ...mapActions('dashboard', ['getClientDashboard']),
    sumRows(list) {
      const acc = ZERO();
      (list || []).forEach(b => {
        acc.total += b.total; acc.age0to15 += b.age0to15; acc.age15to30 += b.age15to30;
        acc.age30plus += b.age30plus; acc.ftd += b.ftd; acc.std += b.std; acc.neverStd += b.neverStd;
      });
      acc.stdPct = acc.ftd > 0 ? (acc.std / acc.ftd) * 100 : 0;
      return acc;
    },
    pct(v) {
      return `${(v || 0).toFixed(1)}%`;
    },
    pctClass(v) {
      if (v >= 50) return 'cd-pct--high';
      if (v >= 20) return 'cd-pct--mid';
      return 'cd-pct--low';
    },
    barW(v) {
      const n = Math.max(0, Math.min(100, v || 0));
      return (n > 0 ? Math.max(4, n) : 0) + '%';
    },
    deskOrgIds(g) {
      return (g.brands || []).map(b => b.orgId);
    },
    // Open the "send this org's aging report as a task" dialog.
    sendReport(b) {
      this.$refs.sendTaskModal.open({
        orgId: b.orgId,
        orgName: b.name,
        segment: 'age30plus',
        count: b.age30plus,
        range: { from: this.dateRange.from || null, until: this.dateRange.until || null },
      });
    },
    segLabel(segment) {
      const key = {
        total: 'clientDashboard.total',
        age0to15: 'clientDashboard.age0to15',
        age15to30: 'clientDashboard.age15to30',
        age30plus: 'clientDashboard.age30plus',
        ftd: 'clientDashboard.ftd',
        std: 'clientDashboard.std',
        neverStd: 'clientDashboard.neverStd',
      }[segment] || 'clientDashboard.total';
      return this.$t(key);
    },
    // Open the drill-down list of the actual clients behind a clicked cell.
    drill(orgIds, segment, contextName, count) {
      if (!count || !orgIds || !orgIds.length) return; // nothing to show
      this.$refs.listModal.open({
        segment,
        orgIds,
        buIds: this.filter.buIds || [],
        buTypes: this.filter.buTypes || [],
        range: { from: this.dateRange.from || null, until: this.dateRange.until || null },
        title: `${contextName} · ${this.segLabel(segment)}`,
        subtitle: this.segLabel(segment),
      });
    },
    // Open the exact same drill-down list as a full page in a new browser tab.
    openExternal(orgIds, segment, contextName, count) {
      if (!count || !orgIds || !orgIds.length) return;
      const route = this.$router.resolve({
        name: 'clientDashboardList',
        query: {
          segment,
          orgIds: orgIds.join(','),
          buIds: (this.filter.buIds || []).join(','),
          buTypes: (this.filter.buTypes || []).join(','),
          from: this.dateRange.from || '',
          until: this.dateRange.until || '',
          title: `${contextName} · ${this.segLabel(segment)}`,
        },
      });
      window.open(route.href, '_blank');
    },
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (this.inflight) { this.pendingFetch = true; return; }
      this.doFetch();
    },
    doFetch() {
      const payload = {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
      };
      this.inflight = true;
      this.loading = true;
      this.getClientDashboard(payload)
        .then(
          data => { this.rows = Array.isArray(data) ? data : []; },
          err => this.showServerErrorNotification('fetchDataError', 'clientDashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) { this.pendingFetch = false; this.doFetch(); }
        });
    },
  },
};
</script>

<style scoped lang="scss">
.client-dashboard {
  position: relative;

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
}

.kpi-row { margin-top: 0.5rem; }

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28), 0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease;
  min-height: 96px;

  &:hover { transform: translateY(-3px); }

  &__icon {
    position: relative; z-index: 2;
    display: flex; align-items: center; justify-content: center;
    width: 48px; height: 48px; border-radius: 14px;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__body { position: relative; z-index: 2; min-width: 0; }
  &__title {
    font-size: 0.78rem; font-weight: 500; text-transform: uppercase;
    letter-spacing: 0.04em; opacity: 0.86; margin-bottom: 0.2rem;
  }
  &__value { font-size: 1.5rem; font-weight: 700; line-height: 1.1; }
  &__glow {
    position: absolute; top: -40%; right: -20%; width: 200px; height: 200px;
    border-radius: 50%; background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
    pointer-events: none; z-index: 1;
  }
  &--primary { background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%); }
  &--success { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
  &--warning { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
  &--info    { background: linear-gradient(135deg, #818cf8 0%, #6366f1 55%, #4f46e5 100%); }
}

.panel-card {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 24px -16px rgba(20, 24, 50, 0.22), 0 1px 3px rgba(20, 24, 50, 0.04);
  overflow: hidden;

  &__header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 1rem 1.25rem; border-bottom: 1px solid rgba(110, 117, 159, 0.12);
    gap: 0.5rem; flex-wrap: wrap;
  }
  &__title-wrap { display: flex; align-items: center; gap: 0.65rem; }
  &__icon-chip {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 123, 255, 0.14), rgba(0, 123, 255, 0.04)); color: #007BFF;
    &--alt { background: linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.04)); color: #10b981; }
  }
  &__title { font-size: 0.98rem; font-weight: 600; color: #1f2a44; }
}

/* ===== Tables ===== */
.cd-table-wrap { overflow-x: auto; }
.cd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  thead th {
    position: sticky; top: 0;
    background: #f7f9fc;
    color: #6e6b7b;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    padding: 0.6rem 1rem;
    white-space: nowrap;
    border-bottom: 1px solid rgba(110, 117, 159, 0.14);
  }
  td { padding: 0.5rem 1rem; border-bottom: 1px solid rgba(110, 117, 159, 0.08); color: #2f2b50; }
  .text-right { text-align: right; }
  .cd-th--brand, .cd-td--brand { text-align: left; }
  .cd-td--brand { font-weight: 500; padding-left: 2rem; }
  .cd-brand-name { vertical-align: middle; }
  .cd-send-btn {
    border: none;
    background: rgba(0, 123, 255, 0.1);
    color: #007BFF;
    width: 24px;
    height: 24px;
    border-radius: 7px;
    font-size: 0.72rem;
    margin-left: 0.5rem;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease, background 0.15s ease, transform 0.15s ease;
  }
  .cd-brand:hover .cd-send-btn { opacity: 1; }
  .cd-send-btn:hover { background: #007BFF; color: #fff; transform: translateY(-1px); }
  .cd-th--total, .cd-td--total { font-weight: 700; }

  tbody tr:hover td { background: rgba(0, 123, 255, 0.035); }

  .cd-group td {
    background: linear-gradient(90deg, rgba(115, 103, 240, 0.12), rgba(115, 103, 240, 0.02));
    color: #5e50ee;
    font-weight: 700;
    padding: 0.45rem 1rem;
  }
  .cd-group__row { display: flex; align-items: center; justify-content: space-between; }
  .cd-group__chip {
    display: inline-flex;
    align-items: center;
    text-transform: uppercase;
    font-size: 0.74rem;
    letter-spacing: 0.05em;
  }
  .cd-group__count {
    font-size: 0.72rem;
    font-weight: 700;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.12);
    padding: 1px 9px;
    border-radius: 8px;
  }

  .cd-brand td { transition: background 0.12s ease; }
  .cd-strong { font-weight: 700; }

  .cd-chip {
    display: inline-block;
    min-width: 36px;
    padding: 2px 9px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 0.78rem;
  }
  /* Clickable drill-down cells */
  .cd-clickable {
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
    &:hover { transform: translateY(-1px); filter: brightness(0.97); }
  }
  .cd-link {
    color: #007BFF;
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
    font-weight: 700;
    &:hover { color: #0062cc; }
  }
  .cd-chip.cd-clickable:hover { box-shadow: 0 4px 10px -4px rgba(20, 24, 50, 0.3); }

  .cd-chip--good { background: rgba(40, 199, 111, 0.14); color: #28a866; }
  .cd-chip--warn { background: rgba(245, 159, 11, 0.16); color: #d98a06; }
  .cd-chip--bad  { background: rgba(234, 84, 85, 0.14); color: #e0484a; }
  .cd-chip--zero { background: transparent; color: #c2c7d6; font-weight: 500; }

  .cd-bar { display: flex; align-items: center; gap: 9px; }
  .cd-bar__track {
    flex: 1;
    min-width: 64px;
    height: 7px;
    border-radius: 6px;
    background: rgba(110, 117, 159, 0.14);
    overflow: hidden;
  }
  .cd-bar__fill {
    height: 100%;
    border-radius: 6px;
    transition: width 0.35s ease;
    &.cd-pct--high { background: linear-gradient(90deg, #28c76f, #48da89); }
    &.cd-pct--mid  { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
    &.cd-pct--low  { background: linear-gradient(90deg, #ea5455, #f1707a); }
  }
  .cd-bar__val { min-width: 44px; text-align: right; font-weight: 700; font-size: 0.76rem; color: #2f2b50; }
  .cd-subtotal td {
    background: rgba(110, 117, 159, 0.05);
    font-weight: 600;
    color: #4b4b63;
    border-bottom: 1px solid rgba(110, 117, 159, 0.16);
  }
  tfoot .cd-grand td {
    background: linear-gradient(90deg, rgba(0, 123, 255, 0.12), rgba(0, 123, 255, 0.03));
    color: #0062cc;
    font-weight: 800;
    border-top: 2px solid rgba(0, 123, 255, 0.25);
  }
}

.cd-pct {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.78rem;
  &--high { background: rgba(40, 199, 111, 0.15); color: #28a866; }
  &--mid  { background: rgba(245, 159, 11, 0.15); color: #d98a06; }
  &--low  { background: rgba(234, 84, 85, 0.13);  color: #e0484a; }
}

/* ===== Dark mode ===== */
.client-dashboard--dark {
  .panel-card { background: #232c3e; box-shadow: 0 10px 24px -16px rgba(0, 0, 0, 0.6); }
  .panel-card__header { border-bottom-color: rgba(255, 255, 255, 0.08); }
  .panel-card__title { color: #d0d2d6; }

  .cd-table {
    thead th { background: #1a2130; color: #8b93ab; border-bottom-color: rgba(255, 255, 255, 0.07); }
    td { color: #d0d2d6; border-bottom-color: rgba(255, 255, 255, 0.05); }
    tbody tr:hover td { background: rgba(123, 175, 255, 0.06); }
    .cd-group td { background: linear-gradient(90deg, rgba(115, 103, 240, 0.22), rgba(115, 103, 240, 0.04)); color: #b3a9ff; }
    .cd-group__count { color: #c4bcff; background: rgba(115, 103, 240, 0.22); }
    .cd-subtotal td { background: rgba(255, 255, 255, 0.03); color: #aeb6c9; border-bottom-color: rgba(255,255,255,0.1); }
    tfoot .cd-grand td { background: linear-gradient(90deg, rgba(77, 163, 255, 0.2), rgba(77, 163, 255, 0.04)); color: #8fc1ff; border-top-color: rgba(77,163,255,0.3); }
    .cd-chip--zero { color: #5a6377; }
    .cd-link { color: #6cb2ff; &:hover { color: #8fc6ff; } }
    .cd-bar__track { background: rgba(255, 255, 255, 0.09); }
    .cd-bar__val { color: #d0d2d6; }
  }
}
</style>
